import { useState, useEffect } from 'react';
import { KeyRound } from 'lucide-react';
import styles from './AdminPage.module.css';

const ADMIN_TOKEN_KEY = 'peapil_admin_token';

type NavKey = 'reset-password';

export default function AdminPage() {
  const [isAuthed, setIsAuthed] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthing, setIsAuthing] = useState(false);

  const [activeNav, setActiveNav] = useState<NavKey>('reset-password');

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ email: string; password: string; mustChangeSet?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(ADMIN_TOKEN_KEY)) {
      setIsAuthed(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminPassword) return;

    setIsAuthing(true);
    setAuthError(null);

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setAuthError(data.error || 'Sign in failed. Please try again.');
        return;
      }

      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setIsAuthed(true);
      setAdminPassword('');
    } catch {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setIsAuthing(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      setIsAuthed(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setCopied(false);

    try {
      const response = await fetch('/api/admin/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: trimmed }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        setIsAuthed(false);
        setError('Your session expired. Please sign in again.');
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          setError('No user found with that email address.');
        } else {
          setError(data.error || 'Something went wrong. Please try again.');
        }
        return;
      }

      setResult({ email: data.email, password: data.password, mustChangeSet: data.mustChangeSet });
      setEmail('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAuthed(false);
    setResult(null);
    setError(null);
    setEmail('');
  };

  if (!isAuthed) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <div className={styles.loginLogo}>
            <img src="/logo-sq.png" alt="Peapil Health" />
          </div>
          <h1 className={styles.loginTitle}>Admin Portal</h1>
          <p className={styles.loginSubtitle}>Sign in to continue</p>

          <form onSubmit={handleLogin} className={styles.form}>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Admin password"
              className={styles.input}
              required
              autoFocus
              disabled={isAuthing}
            />
            <button type="submit" className={styles.button} disabled={isAuthing}>
              {isAuthing ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {authError && <div className={styles.errorMessage}>{authError}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.portal}>
      <div className={styles.topBar}>
        <div className={styles.brand}>
          <img src="/logo-sq.png" alt="Peapil Health" />
          <span className={styles.brandText}>Peapil Health</span>
          <span className={styles.brandSub}>Admin Portal</span>
        </div>
        <button className={styles.signOutBtn} onClick={handleLogout}>
          Sign out
        </button>
      </div>

      <div className={styles.body}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarLabel}>Tools</div>
          <button
            className={`${styles.navItem} ${activeNav === 'reset-password' ? styles.navItemActive : ''}`}
            onClick={() => setActiveNav('reset-password')}
          >
            <KeyRound size={18} className={styles.navIcon} />
            Reset Password
          </button>
        </aside>

        <main className={styles.main}>
          {activeNav === 'reset-password' && (
            <section className={styles.panel}>
              <h2 className={styles.panelTitle}>Reset User Password</h2>
              <p className={styles.panelSubtitle}>
                Enter a user's email address to generate a new temporary password they can use to sign in to the mobile app.
              </p>

              <form onSubmit={handleReset}>
                <label className={styles.fieldLabel} htmlFor="user-email">
                  User email address
                </label>
                <div className={styles.formRow}>
                  <input
                    id="user-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                    className={styles.input}
                    required
                    disabled={isLoading}
                  />
                  <button type="submit" className={styles.button} disabled={isLoading}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>

              {error && <div className={styles.errorMessage}>{error}</div>}

              {result && (
                <div className={styles.resultCard}>
                  <p className={styles.resultLabel}>New password for</p>
                  <p className={styles.resultEmail}>{result.email}</p>

                  <div className={styles.passwordBox}>
                    <span className={styles.passwordText}>{result.password}</span>
                    <button type="button" className={styles.copyBtn} onClick={handleCopy}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <p className={styles.resultHint}>
                    Share this password with the user. They can sign in to the mobile app with their email and this temporary password.
                    {result.mustChangeSet
                      ? ' They will be required to change it on next sign in.'
                      : ' Note: could not flag the account to require a password change — please follow up manually.'}
                  </p>
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
